import type {
  AvailabilityResponse,
  BookingConfirmation,
  BookingRequestRequest,
  BookingRequestResponse,
  CreateBookingRequest,
  CreateBookingResponse,
  CreatePaymentResponse,
  Experience,
  ExperienceListItem,
  HomeResponse,
} from '../types';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';
const API_KEY = import.meta.env.VITE_API_KEY ?? '';

const toSnake = (s: string) => s.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
const toCamel = (s: string) => s.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

function convertKeys(value: unknown, fn: (k: string) => string): unknown {
  if (Array.isArray(value)) return value.map((v) => convertKeys(v, fn));
  if (value && typeof value === 'object' && value.constructor === Object) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [fn(k), convertKeys(v, fn)]),
    );
  }
  return value;
}

interface ReqOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function req<T>(path: string, options: ReqOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(API_KEY ? { 'X-Api-Key': API_KEY } : {}),
    ...(options.headers ?? {}),
  };

  const body =
    options.body !== undefined ? JSON.stringify(convertKeys(options.body, toSnake)) : undefined;

  const res = await fetch(`${BASE}${path}`, { method: options.method, headers, body });
  if (!res.ok) {
    let message = `API ${res.status}`;
    const text = await res.text().catch(() => '');
    if (text) {
      try {
        const parsed = JSON.parse(text);
        if (typeof parsed === 'string') message = parsed;
        else if (parsed?.message) message = String(parsed.message);
        else if (parsed?.data?.message) message = String(parsed.data.message);
        else message = text;
      } catch {
        message = text;
      }
    }
    throw new ApiError(res.status, message);
  }

  const json = await res.json();
  const payload = json?.data ?? json;
  return convertKeys(payload, toCamel) as T;
}

export const api = {
  listExperiences: () => req<ExperienceListItem[]>('/experience-catalog'),

  getHome: () => req<HomeResponse>('/experience-catalog/home'),

  getExperience: (code: string) =>
    req<Experience>(`/experience-catalog/code/${encodeURIComponent(code)}`),

  getAvailableSlots: (code: string, date: string, partySize: number) =>
    req<AvailabilityResponse>(
      `/booking/available-slots?experienceCode=${encodeURIComponent(code)}&date=${date}&partySize=${partySize}`,
    ),

  createBooking: (body: CreateBookingRequest) =>
    req<CreateBookingResponse>('/booking/create', { method: 'POST', body }),

  // OnRequest (Sob Consulta). Não cria Booking nem PaymentTransaction —
  // cria Conversation em WaitingAvailability e dispara o pipeline do operador.
  requestBooking: (body: BookingRequestRequest) =>
    req<BookingRequestResponse>('/booking/request', { method: 'POST', body }),

  createPayment: (bookingId: string) =>
    req<CreatePaymentResponse>('/payment/create', { method: 'POST', body: { bookingId } }),

  getConfirmation: (id: string) =>
    req<BookingConfirmation>(`/booking/confirmation/${encodeURIComponent(id)}`),

  verifyPhoneStart: (phone: string, name: string) =>
    req<{ customerId: number; message: string }>('/customer/verify-phone/start', {
      method: 'POST',
      body: { phone, name },
    }),

  verifyPhoneConfirm: (phone: string, code: string) =>
    req<{ verified: boolean }>('/customer/verify-phone/confirm', {
      method: 'POST',
      body: { phone, code },
    }),
};
